// Service de logging pour le système de lazy loading
// Active les logs uniquement si config.gridLayout.overlay.enabled est true

export type LogCategory = 
    | 'viewport'   // 👁️ Composants entrant/sortant du viewport
    | 'data'       // 📦 Chargement de données API
    | 'media'      // 🖼️ Chargement de médias
    | 'preload'    // ⚡ Préchargement
    | 'queue'      // 📊 État des queues
    | 'perf';      // ⏱️ Métriques de performance

interface LogConfig {
    enabled: boolean;
}

class LazyLoadLoggerClass {
    private config: LogConfig = { enabled: false };
    
    private readonly categoryEmojis: Record<LogCategory, string> = {
        viewport: '👁️',
        data: '📦',
        media: '🖼️',
        preload: '⚡',
        queue: '📊',
        perf: '⏱️'
    };

    private readonly categoryColors: Record<LogCategory, string> = {
        viewport: '#3b82f6', // blue
        data: '#10b981',     // green
        media: '#f59e0b',    // amber
        preload: '#8b5cf6',  // violet
        queue: '#ec4899',    // pink
        perf: '#06b6d4'      // cyan
    };

    /**
     * Initialise le logger avec la configuration
     */
    init(enabled: boolean) {
        this.config.enabled = enabled;
        if (enabled) {
            console.log('%c🚀 LazyLoad Logger activé', 'color: #10b981; font-weight: bold; font-size: 14px;');
        }
    }

    /**
     * Active/désactive le logger
     */
    setEnabled(enabled: boolean) {
        this.config.enabled = enabled;
    }

    /**
     * Vérifie si le logger est activé
     */
    isEnabled(): boolean {
        return this.config.enabled;
    }

    /**
     * Log un message avec une catégorie
     */
    log(category: LogCategory, message: string, data?: any) {
        if (!this.config.enabled) return;

        const emoji = this.categoryEmojis[category];
        const color = this.categoryColors[category];
        const timestamp = new Date().toLocaleTimeString('fr-FR', { 
            hour12: false, 
            hour: '2-digit', 
            minute: '2-digit', 
            second: '2-digit',
            fractionalSecondDigits: 3
        });

        const prefix = `%c${emoji} [${category.toUpperCase()}] ${timestamp}`;
        const style = `color: ${color}; font-weight: bold;`;

        if (data !== undefined) {
            console.log(prefix + ' %c' + message, style, 'color: inherit;', data);
        } else {
            console.log(prefix + ' %c' + message, style, 'color: inherit;');
        }
    }

    /**
     * Log pour les composants du viewport
     */
    logViewport(message: string, data?: any) {
        this.log('viewport', message, data);
    }

    /**
     * Log pour le chargement de données
     */
    logData(message: string, data?: any) {
        this.log('data', message, data);
    }

    /**
     * Log pour les médias
     */
    logMedia(message: string, data?: any) {
        this.log('media', message, data);
    }

    /**
     * Log pour le préchargement
     */
    logPreload(message: string, data?: any) {
        this.log('preload', message, data);
    }

    /**
     * Log pour les queues
     */
    logQueue(message: string, data?: any) {
        this.log('queue', message, data);
    }

    /**
     * Log pour les performances
     */
    logPerf(message: string, data?: any) {
        this.log('perf', message, data);
    }

    /**
     * Groupe de logs
     */
    group(category: LogCategory, title: string, collapsed: boolean = false) {
        if (!this.config.enabled) return;

        const emoji = this.categoryEmojis[category];
        const color = this.categoryColors[category];
        
        if (collapsed) {
            console.groupCollapsed(`%c${emoji} ${title}`, `color: ${color}; font-weight: bold;`);
        } else {
            console.group(`%c${emoji} ${title}`, `color: ${color}; font-weight: bold;`);
        }
    }

    /**
     * Fin du groupe
     */
    groupEnd() {
        if (!this.config.enabled) return;
        console.groupEnd();
    }

    /**
     * Tableau de données
     */
    table(category: LogCategory, title: string, data: any) {
        if (!this.config.enabled) return;

        const emoji = this.categoryEmojis[category];
        const color = this.categoryColors[category];
        
        console.log(`%c${emoji} ${title}`, `color: ${color}; font-weight: bold;`);
        console.table(data);
    }

    /**
     * Mesure de performance
     */
    time(label: string) {
        if (!this.config.enabled) return;
        console.time(`⏱️ ${label}`);
    }

    /**
     * Fin de mesure de performance
     */
    timeEnd(label: string) {
        if (!this.config.enabled) return;
        console.timeEnd(`⏱️ ${label}`);
    }

    /**
     * Warning
     */
    warn(category: LogCategory, message: string, data?: any) {
        if (!this.config.enabled) return;

        const emoji = this.categoryEmojis[category];
        console.warn(`${emoji} [${category.toUpperCase()}] ${message}`, data);
    }

    /**
     * Erreur
     */
    error(category: LogCategory, message: string, error?: any) {
        if (!this.config.enabled) return;

        const emoji = this.categoryEmojis[category];
        console.error(`${emoji} [${category.toUpperCase()}] ${message}`, error);
    }
}

// Export singleton
export const LazyLoadLogger = new LazyLoadLoggerClass();



